import { Building2, Package, PoundSterling, Truck, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import type { CompanyGroupMembersResponse } from '../../lib/api';

interface GroupStatsCardsProps {
  stats: CompanyGroupMembersResponse['stats'];
  totalUsers: number;
  formatCurrencyFromBase: (value: number) => string;
}

/**
 * Summary KPI cards for a customer group detail page.
 */
export function GroupStatsCards({ stats, totalUsers, formatCurrencyFromBase }: GroupStatsCardsProps) {
  const orderValue = parseFloat(stats.total_order_value || '0');

  const items = [
    {
      label: 'Companies',
      value: stats.total_companies,
      hint: 'member companies',
      icon: Building2,
    },
    {
      label: 'Users',
      value: totalUsers,
      hint: 'across all companies',
      icon: Users,
    },
    {
      label: 'Total Orders',
      value: stats.total_orders,
      hint: 'sales orders',
      icon: Package,
    },
    {
      label: 'Total Orders Value',
      value: formatCurrencyFromBase(orderValue),
      hint: 'converted to your display currency',
      icon: PoundSterling,
      isFormatted: true,
    },
    {
      label: 'Total Number of Shipments',
      value: stats.total_shipments,
      hint: 'ASN shipments',
      icon: Truck,
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      {items.map(({ label, value, hint, icon: Icon, isFormatted }) => (
        <Card key={label}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <Icon className="h-4 w-4" />
              {label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`font-bold tracking-tight ${isFormatted ? 'text-2xl' : 'text-3xl'}`}>
              {value}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{hint}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
